/* ============================================================================
   Nexus Dex blank Trainer CSV template
   ----------------------------------------------------------------------------
   Mirrors the exact column layout the Trainer CSV parser expects, so a user
   can either fill it out in their own offline spreadsheet or paste rows from
   any tracker that exports the same headers.

   The two example rows use cleanly fictional, non-trademarked names ("My
   Pidgey", "My Bulbasaur") to show what the cells look like without making
   the file feel like a sample of someone else's collection.
   ============================================================================ */

const TEMPLATE_HEADERS = [
  "Index",
  "Name",
  "Form",
  "Pokemon Number",
  "Gender",
  "CP",
  "HP",
  "Atk IV",
  "Def IV",
  "Sta IV",
  "IV Avg",
  "Level Min",
  "Level Max",
  "Quick Move",
  "Charge Move 1",
  "Charge Move 2",
  "Catch Date",
  "Weight",
  "Height",
  "Lucky",
  "Shiny",
  "Shadow/Purified",
  "Has 3rd Move",
  "Lat",
  "Lng",
  "Catch Place",
] as const;

// Two example rows to show the expected format. Lat/Lng intentionally blank
// so the template doesn't ship a fake location footer.
const EXAMPLE_ROWS: string[][] = [
  [
    "1",
    "My Pidgey",
    "Normal",
    "16",
    "M",
    "234",
    "60",
    "12",
    "14",
    "13",
    "85.6",
    "20",
    "20.5",
    "Quick Attack",
    "Aerial Ace",
    "",
    "2026-04-28 09:30:00",
    "1.8",
    "0.3",
    "0",
    "0",
    "Normal",
    "0",
    "",
    "",
    "",
  ],
  [
    "2",
    "My Bulbasaur",
    "Normal",
    "1",
    "F",
    "654",
    "82",
    "15",
    "15",
    "15",
    "100",
    "25",
    "25",
    "Vine Whip",
    "Sludge Bomb",
    "Power Whip",
    "2026-04-27 18:14:22",
    "6.9",
    "0.7",
    "0",
    "1",
    "Normal",
    "1",
    "",
    "",
    "",
  ],
];

function escapeCell(value: string): string {
  // Quote any cell containing comma, quote, or newline; double inner quotes.
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/**
 * Build the blank-template CSV text. Returns a string ready to download.
 */
export function buildTrainerCsvTemplate(): string {
  const lines: string[] = [];
  lines.push(TEMPLATE_HEADERS.map(escapeCell).join(","));
  for (const row of EXAMPLE_ROWS) {
    lines.push(row.map(escapeCell).join(","));
  }
  return lines.join("\r\n") + "\r\n";
}

/**
 * Trigger a browser download of the blank template CSV. Uses an in-memory
 * Blob so nothing is fetched from the network.
 */
export function downloadTrainerCsvTemplate(filename = "nexus-dex-trainer-csv-template.csv"): void {
  const csv = buildTrainerCsvTemplate();
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke after a small tick to let the download start in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
